import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useStores } from './stores';
import MainLayout from './components/layout/MainLayout';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';
import HomePage from './pages/HomePage';
import ProductsPage from './pages/products/ProductsPage';
import ProductDetailPage from './pages/products/ProductDetailPage';
import CartPage from './pages/cart/CartPage';
import CheckoutPage from './pages/cart/CheckoutPage';
import OrdersPage from './pages/orders/OrdersPage';
import OrderDetailPage from './pages/orders/OrderDetailPage';
import OrderConfirmationPage from './pages/orders/OrderConfirmationPage';
import ServicesPage from './pages/services/ServicesPage';
import ServiceDetailPage from './pages/services/ServiceDetailPage';
import MyAppointmentsPage from './pages/appointments/MyAppointmentsPage';
import MyPetsPage from './pages/pets/MyPetsPage';
import MyFavoritesPage from './pages/favorites/MyFavoritesPage';
import AddEditPetPage from './pages/pets/AddEditPetPage';
import PetCalendarPage from './pages/pets/PetCalendarPage';
import PremiumPage from './pages/premium/PremiumPage';
import ProfilePage from './pages/profile/ProfilePage';
import PartnerDashboard from './pages/partner/dashboard/PartnerDashboard';
import PartnerOrdersList from './pages/partner/orders/PartnerOrdersList';
import PartnerOrderDetail from './pages/partner/orders/PartnerOrderDetail';
import InventoryList from './pages/partner/inventory/InventoryList';
import InventoryAdd from './pages/partner/inventory/InventoryAdd';
import InventoryEdit from './pages/partner/inventory/InventoryEdit';
import ServicesList from './pages/partner/services/ServicesList';
import ServiceAddEdit from './pages/partner/services/ServiceAddEdit';
import ServiceAvailability from './pages/partner/services/ServiceAvailability';
import ServiceHolidays from './pages/partner/services/ServiceHolidays';
import PartnerAppointmentsList from './pages/partner/appointments/PartnerAppointmentsList';
import PartnerAppointmentDetail from './pages/partner/appointments/PartnerAppointmentDetail';
import RevenueReport from './pages/partner/reports/RevenueReport';
import PartnerReviewsPage from './pages/partner/reviews/PartnerReviewsPage';
import PartnerInvoices from './pages/partner/invoices/PartnerInvoices';
import ItemPackagesPage from './pages/partner/ItemPackagesPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import ProductQueue from './pages/admin/ProductQueue';
import ProductEnrich from './pages/admin/ProductEnrich';
import ProductList from './pages/admin/ProductList';
import UserList from './pages/admin/UserList';
import PartnerList from './pages/admin/PartnerList';
import PartnerDetail from './pages/admin/PartnerDetail';
import ServiceCategoryList from './pages/admin/ServiceCategoryList';
import CategorySuggestionList from './pages/admin/CategorySuggestionList';
import AnimalCategoryList from './pages/admin/AnimalCategoryList';
import ProductCategoryList from './pages/admin/ProductCategoryList';
import BreedList from './pages/admin/BreedList';
import AdminAnimalsList from './pages/admin/AdminAnimalsList';
import InvoiceManagement from './pages/admin/InvoiceManagement';
import InquiryManagement from './pages/admin/InquiryManagement';
import AdminReviews from './pages/admin/AdminReviews';
import AdminCoupons from './pages/admin/AdminCoupons';
import CouponManagement from './pages/partner/CouponManagement';
import TeamManagement from './pages/partner/team/TeamManagement';
import PartnerProfileEdit from './pages/partner/profile/PartnerProfileEdit';
import NgoList from './pages/admin/NgoList';
import AdminAppointmentList from './pages/admin/AdminAppointmentList';
import AdminOrderList from './pages/admin/AdminOrderList';
import AdminServiceList from './pages/admin/AdminServiceList';
import BundleRequestList from './pages/admin/BundleRequestList';
import MyInquiriesPage from './pages/user/MyInquiriesPage';
import NotificationsPage from './pages/notifications/NotificationsPage';
import NotificationPreferencesPage from './pages/notifications/NotificationPreferencesPage';
import PartnersListPage from './pages/partners/PartnersListPage';
import PartnerMapPage from './pages/partners/PartnerMapPage';
import PartnerProfilePage from './pages/partners/PartnerProfilePage';
import FAQPage from './pages/support/FAQPage';
import TermsPage from './pages/legal/TermsPage';
import PrivacyPage from './pages/legal/PrivacyPage';
import CookiesPage from './pages/legal/CookiesPage';
import BusinessTermsPage from './pages/legal/BusinessTermsPage';
import TwoFactorVerify from './components/auth/TwoFactorVerify';

// Protected Route Component
const ProtectedRoute = observer(({ children, allowedRoles = [] }) => {
  const { authStore } = useStores();

  if (!authStore.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(authStore.user?.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
});

// Public Only Route (redirect to home if already authenticated)
const PublicOnlyRoute = observer(({ children }) => {
  const { authStore } = useStores();

  if (authStore.isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
});

function App() {
  return (
    <>
      <TwoFactorVerify />
      <Routes>
      {/* Routes with Layout */}
      <Route path="/" element={<MainLayout />}>
        <Route index element={<HomePage />} />

        {/* Public Routes */}
        <Route path="faq" element={<FAQPage />} />
        <Route path="terms" element={<TermsPage />} />
        <Route path="privacy" element={<PrivacyPage />} />
        <Route path="cookies" element={<CookiesPage />} />
        <Route path="business-terms" element={<BusinessTermsPage />} />

        {/* Protected Routes - Products (require login) */}
        <Route
          path="products"
          element={
            <ProtectedRoute>
              <ProductsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="products/:id"
          element={
            <ProtectedRoute>
              <ProductDetailPage />
            </ProtectedRoute>
          }
        />

        {/* Protected Routes - Services (require login) */}
        <Route
          path="services"
          element={
            <ProtectedRoute>
              <ServicesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="services/:id"
          element={
            <ProtectedRoute>
              <ServiceDetailPage />
            </ProtectedRoute>
          }
        />

        {/* Protected Routes - Partners */}
        <Route
          path="partners"
          element={
            <ProtectedRoute>
              <PartnersListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="partners/map"
          element={
            <ProtectedRoute>
              <PartnerMapPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="partners/:id"
          element={
            <ProtectedRoute>
              <PartnerProfilePage />
            </ProtectedRoute>
          }
        />

        {/* Protected Routes - Cart & Orders */}
        <Route
          path="cart"
          element={
            <ProtectedRoute>
              <CartPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="checkout"
          element={
            <ProtectedRoute>
              <CheckoutPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="order-confirmation"
          element={
            <ProtectedRoute>
              <OrderConfirmationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="orders"
          element={
            <ProtectedRoute>
              <OrdersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="orders/:id"
          element={
            <ProtectedRoute>
              <OrderDetailPage />
            </ProtectedRoute>
          }
        />

        {/* Protected Routes - Appointments */}
        <Route
          path="my-appointments"
          element={
            <ProtectedRoute>
              <MyAppointmentsPage />
            </ProtectedRoute>
          }
        />

        {/* Protected Routes - Pets */}
        <Route
          path="my-pets"
          element={
            <ProtectedRoute>
              <MyPetsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="pets/add"
          element={
            <ProtectedRoute>
              <AddEditPetPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="pets/edit/:id"
          element={
            <ProtectedRoute>
              <AddEditPetPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="pets/calendar"
          element={
            <ProtectedRoute>
              <PetCalendarPage />
            </ProtectedRoute>
          }
        />

        {/* Protected Routes - Premium */}
        <Route
          path="premium"
          element={
            <ProtectedRoute>
              <PremiumPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="premium/success"
          element={
            <ProtectedRoute>
              <PremiumPage />
            </ProtectedRoute>
          }
        />

        {/* Protected Routes - Favorites */}
        <Route
          path="my-favorites"
          element={
            <ProtectedRoute>
              <MyFavoritesPage />
            </ProtectedRoute>
          }
        />

        {/* Protected Routes - Profile */}
        <Route
          path="profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />

        {/* Protected Routes - Inquiries */}
        <Route
          path="my-inquiries"
          element={
            <ProtectedRoute>
              <MyInquiriesPage />
            </ProtectedRoute>
          }
        />

        {/* Protected Routes - Notifications */}
        <Route
          path="notifications"
          element={
            <ProtectedRoute>
              <NotificationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="profile/notifications"
          element={
            <ProtectedRoute>
              <NotificationPreferencesPage />
            </ProtectedRoute>
          }
        />

        {/* Partner Routes - Protected (PARTNER and ADMIN only) */}
        <Route
          path="partner/dashboard"
          element={
            <ProtectedRoute allowedRoles={['PARTNER', 'ADMIN']}>
              <PartnerDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="partner/orders"
          element={
            <ProtectedRoute allowedRoles={['PARTNER', 'ADMIN']}>
              <PartnerOrdersList />
            </ProtectedRoute>
          }
        />
        <Route
          path="partner/orders/:id"
          element={
            <ProtectedRoute allowedRoles={['PARTNER', 'ADMIN']}>
              <PartnerOrderDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="partner/inventory"
          element={
            <ProtectedRoute allowedRoles={['PARTNER', 'ADMIN']}>
              <InventoryList />
            </ProtectedRoute>
          }
        />
        <Route
          path="partner/inventory/add"
          element={
            <ProtectedRoute allowedRoles={['PARTNER', 'ADMIN']}>
              <InventoryAdd />
            </ProtectedRoute>
          }
        />
        <Route
          path="partner/inventory/edit/:id"
          element={
            <ProtectedRoute allowedRoles={['PARTNER', 'ADMIN']}>
              <InventoryEdit />
            </ProtectedRoute>
          }
        />
        <Route
          path="partner/services"
          element={
            <ProtectedRoute allowedRoles={['PARTNER', 'ADMIN']}>
              <ServicesList />
            </ProtectedRoute>
          }
        />
        <Route
          path="partner/services/add"
          element={
            <ProtectedRoute allowedRoles={['PARTNER', 'ADMIN']}>
              <ServiceAddEdit />
            </ProtectedRoute>
          }
        />
        <Route
          path="partner/services/edit/:id"
          element={
            <ProtectedRoute allowedRoles={['PARTNER', 'ADMIN']}>
              <ServiceAddEdit />
            </ProtectedRoute>
          }
        />
        <Route
          path="partner/services/:id/availability"
          element={
            <ProtectedRoute allowedRoles={['PARTNER', 'ADMIN']}>
              <ServiceAvailability />
            </ProtectedRoute>
          }
        />
        <Route
          path="partner/services/:id/holidays"
          element={
            <ProtectedRoute allowedRoles={['PARTNER', 'ADMIN']}>
              <ServiceHolidays />
            </ProtectedRoute>
          }
        />
        <Route
          path="partner/appointments"
          element={
            <ProtectedRoute allowedRoles={['PARTNER', 'ADMIN']}>
              <PartnerAppointmentsList />
            </ProtectedRoute>
          }
        />
        <Route
          path="partner/appointments/:id"
          element={
            <ProtectedRoute allowedRoles={['PARTNER', 'ADMIN']}>
              <PartnerAppointmentDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="partner/revenue-report"
          element={
            <ProtectedRoute allowedRoles={['PARTNER', 'ADMIN']}>
              <RevenueReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="partner/reviews"
          element={
            <ProtectedRoute allowedRoles={['PARTNER', 'ADMIN']}>
              <PartnerReviewsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="partner/invoices"
          element={
            <ProtectedRoute allowedRoles={['PARTNER', 'ADMIN']}>
              <PartnerInvoices />
            </ProtectedRoute>
          }
        />
        <Route
          path="partner/coupons"
          element={
            <ProtectedRoute allowedRoles={['PARTNER', 'ADMIN']}>
              <CouponManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="partner/team"
          element={
            <ProtectedRoute allowedRoles={['PARTNER', 'ADMIN']}>
              <TeamManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="partner/profile"
          element={
            <ProtectedRoute allowedRoles={['PARTNER', 'ADMIN']}>
              <PartnerProfileEdit />
            </ProtectedRoute>
          }
        />
        <Route
          path="partner/packages"
          element={
            <ProtectedRoute allowedRoles={['PARTNER', 'ADMIN']}>
              <ItemPackagesPage />
            </ProtectedRoute>
          }
        />

        {/* Admin Routes - Protected (ADMIN only) */}
        <Route
          path="admin/dashboard"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/products/queue"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <ProductQueue />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/products/enrich/:id"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <ProductEnrich />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/products"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <ProductList />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/users"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <UserList />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/partners"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <PartnerList />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/partners/:id"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <PartnerDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/service-categories"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <ServiceCategoryList />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/category-suggestions"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <CategorySuggestionList />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/animal-categories"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <AnimalCategoryList />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/product-categories"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <ProductCategoryList />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/breeds"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <BreedList />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/animals"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <AdminAnimalsList />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/ngos"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <NgoList />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/invoices"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <InvoiceManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/inquiries"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <InquiryManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/reviews"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <AdminReviews />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/coupons"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <AdminCoupons />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/appointments"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <AdminAppointmentList />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/orders"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <AdminOrderList />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/services"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <AdminServiceList />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/bundles"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <BundleRequestList />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Auth Routes (No Layout) */}
      <Route
        path="/login"
        element={
          <PublicOnlyRoute>
            <LoginPage />
          </PublicOnlyRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicOnlyRoute>
            <RegisterPage />
          </PublicOnlyRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicOnlyRoute>
            <ForgotPasswordPage />
          </PublicOnlyRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicOnlyRoute>
            <ResetPasswordPage />
          </PublicOnlyRoute>
        }
      />
      <Route
        path="/verify-email"
        element={
          <PublicOnlyRoute>
            <VerifyEmailPage />
          </PublicOnlyRoute>
        }
      />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

export default observer(App);
